import { Phone, Mail, Check, Eye } from 'lucide-react'
import type { Customer } from '@/types'
import { BlockedBadge } from './BlockedBadge'

export interface CustomerCardProps {
  customer: Customer
  isSelected: boolean
  onSelect: (customer: Customer) => void
  onOpenDetails?: (customer: Customer) => void
  isFocused?: boolean
}

// Loyalty tier styles
const tierStyles: Record<string, string> = {
  VIP: 'bg-purple-100 text-purple-700',
  'VIP (20%)': 'bg-purple-100 text-purple-700',
  Gold: 'bg-yellow-100 text-yellow-700',
  Silver: 'bg-gray-200 text-gray-700',
  Standard: 'bg-gray-100 text-gray-600',
  'Margin (15%)': 'bg-blue-100 text-blue-700',
  Medical: 'bg-green-100 text-green-700',
  Wholesale: 'bg-orange-100 text-orange-700',
}

function getCustomerName(customer: Customer): string {
  if (customer.first_name) {
    return `${customer.first_name} ${customer.last_name || ''}`.trim()
  }
  return customer.name || 'Customer'
}

function getCustomerTier(customer: Customer): string {
  return customer.price_tier || customer.priceTier || 'Standard'
}

function getCustomerPoints(customer: Customer): number {
  return customer.points ?? 0
}

function getCurrentPurchase(customer: Customer): number {
  return customer.daily_purchased_grams ?? customer.currentPurchase ?? 0
}

// Grid view - Larger customer card matching product card style
export function CustomerCard({ customer, isSelected, onSelect, onOpenDetails, isFocused }: CustomerCardProps) {
  const name = getCustomerName(customer)
  const tier = getCustomerTier(customer)
  const points = getCustomerPoints(customer)
  const currentPurchase = getCurrentPurchase(customer)
  const tierStyle = tierStyles[tier] || tierStyles.Standard

  return (
    <div
      onClick={() => onSelect(customer)}
      className={`bg-white rounded-2xl border p-3 hover:shadow-lg hover:shadow-[#44BC78]/5 transition-all cursor-pointer h-full flex flex-col relative ${
        isFocused
          ? 'border-green-400 shadow-lg ring-2 ring-green-300'
          : isSelected
            ? 'border-green-500 ring-2 ring-green-100'
            : 'border-gray-200 hover:border-green-400'
      }`}
    >
      {/* Name */}
      <div className="flex items-start justify-between mb-2 gap-2">
        <h3 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2">
          {name}
        </h3>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isSelected && (
            <span className="w-5 h-5 bg-green-500 text-white text-xs rounded-full flex items-center justify-center">
              <Check size={12} />
            </span>
          )}
          {onOpenDetails && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onOpenDetails(customer)
              }}
              className="w-6 h-6 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors cursor-pointer"
              aria-label="View customer details"
              title="View details"
            >
              <Eye size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Badges row: Loyalty, Points, Today's purchase */}
      <div className="flex items-center gap-1.5 text-xs mb-2 flex-wrap">
        {customer.is_blocked && <BlockedBadge />}
        <span className={`px-1.5 py-0.5 rounded font-medium ${tierStyle}`}>
          {tier}
        </span>
        {points > 0 && (
          <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
            {points} pts
          </span>
        )}
        {currentPurchase > 0 && (
          <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">
            {currentPurchase}g today
          </span>
        )}
        {customer.is_medical && (
          <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
            Medical
          </span>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1"></div>

      {/* Bottom: Contact info */}
      <div className="pt-2 border-t border-gray-100 mt-auto space-y-1">
        {customer.phone && (
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <Phone size={12} className="flex-shrink-0" />
            <span>{customer.phone}</span>
          </div>
        )}
        {customer.email && (
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <Mail size={12} className="flex-shrink-0" />
            <span className="truncate">{customer.email}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// List view - Horizontal customer row matching product row style
export function CustomerRowCard({ customer, isSelected, onSelect, onOpenDetails, isFocused }: CustomerCardProps) {
  const name = getCustomerName(customer)
  const tier = getCustomerTier(customer)
  const points = getCustomerPoints(customer)
  const currentPurchase = getCurrentPurchase(customer)
  const tierStyle = tierStyles[tier] || tierStyles.Standard

  return (
    <div
      onClick={() => onSelect(customer)}
      className={`bg-white rounded-2xl border p-3 hover:shadow-lg hover:shadow-[#44BC78]/5 transition-all cursor-pointer flex items-center gap-3 ${
        isFocused
          ? 'border-green-400 shadow-lg ring-2 ring-green-300'
          : isSelected
            ? 'border-green-500 ring-2 ring-green-100'
            : 'border-gray-200 hover:border-green-400'
      }`}
    >
      {/* Name */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <h3 className="font-semibold text-gray-900 text-base leading-tight">
            {name}
          </h3>
          {isSelected && (
            <span className="w-5 h-5 bg-green-500 text-white text-xs rounded-full flex items-center justify-center">
              <Check size={12} />
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          {customer.phone && (
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Phone size={12} className="flex-shrink-0" />
              <span>{customer.phone}</span>
            </div>
          )}
          {customer.email && (
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Mail size={12} className="flex-shrink-0" />
              <span className="truncate">{customer.email}</span>
            </div>
          )}
        </div>
      </div>

      {/* Badges: Loyalty, Points, Today's purchase */}
      <div className="flex items-center gap-1.5 text-xs flex-shrink-0">
        {customer.is_blocked && <BlockedBadge />}
        <span className={`px-1.5 py-0.5 rounded font-medium ${tierStyle}`}>
          {tier}
        </span>
        {points > 0 && (
          <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
            {points} pts
          </span>
        )}
        {currentPurchase > 0 && (
          <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">
            {currentPurchase}g today
          </span>
        )}
        {onOpenDetails && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onOpenDetails(customer)
            }}
            className="ml-1 w-7 h-7 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors cursor-pointer"
            aria-label="View customer details"
            title="View details"
          >
            <Eye size={14} />
          </button>
        )}
      </div>
    </div>
  )
}
